/* CAN WE ACTUALLY MEASURE THE WORK? — coverage and accountability across the TAM.
 *
 * Brian: "if we cant track it and measure it, we cant measure it."
 * Brian: "there will be 100% coverage and outreach to every single opportunity.
 *         at minimum artwork."
 * Brian, on the Zipcio call: "As an owner, as a manager, I have to be able to
 *         measure what output or what tasks have been done… I have to be able
 *         to say, okay, Jonathan has put out 17 proposals this week. Three of
 *         them have closed."
 *
 * ⚠️ MEASURED LIVE OVER 5,000 RECORDS BEFORE WRITING ANY OF THIS, because a
 * coverage model built on fields that are empty is a dashboard of zeroes
 * wearing a suit:
 *
 *     assigned to a rep          0 of 5,000   (0%)
 *     ready_for_artwork set      0 of 5,000   (0%)
 *     sr_pursuit_status          5,000 of 5,000 read "To Pursue" — ONE value
 *
 * So today the answer to "who owns this opportunity" is nobody, for everything,
 * and the answer to "what stage is it at" is the same word for every record.
 * Neither is a reporting problem. There is nothing to report.
 *
 * ⚠️ AND THE HARDER HALF: MOST OF THE LIFECYCLE HAS NO FIELDS AT ALL.
 *
 * Brian: "im also talking about my team, from outreach, to call scheduled, to
 * video call complete, to proposal sent, to closure, to delivered, to paid, to
 * reviewed."
 *
 * That is TEN stages. THREE of them are measurable today. Seven leave no trace
 * anywhere — the event happens in the real world and the record does not change.
 *
 * And the lifecycle CROSSES FOUR SYSTEMS, which is why this cannot be a HubSpot
 * pipeline report:
 *
 *     outreach → proposal → closed    HubSpot
 *     delivered                       ClickUp order + Shippo in-hands date
 *     paid                            QuickBooks
 *     reviewed                        the reviews system
 *
 * ⚠️ AND THE THREE AFTER "CLOSED" ARE MIRRORS, NEVER AUTHORED HERE. ClickUp
 * owns the order and QBO owns the invoice; writing a delivery date into HubSpot
 * as its home would make the CRM a second source of truth for something it does
 * not own, which is the one rule this codebase does not bend.
 *
 * `sr_pursuit_status` is a single enum and every one of 5,000 records sits on
 * its first value, so it is not currently doing any of this work either.
 *
 * That is what his rule means in practice. You cannot measure a task that has
 * no field to record it — not because the report is missing, but because the
 * event leaves no trace. Building a coverage percentage on top of that would
 * produce a number that moves only when somebody edits a status by hand, which
 * measures diligence at data entry rather than work done.
 *
 * So this module does two things and refuses to do a third:
 *   1. Measures what IS recordable, honestly, including when that is zero.
 *   2. Names the fields that must exist before the rest is measurable at all —
 *      as a spec, not a complaint.
 *   3. Does NOT invent a coverage number out of fields that cannot move.
 *
 * Pure. No React, no fetch.
 */

const S = (v) => String(v == null ? '' : v).trim()

/* =========================================================================
 * THE FUNNEL, AND WHETHER EACH STAGE CAN BE MEASURED TODAY.
 *
 * `field` is the property that records it. `exists: false` means the event can
 * happen in the real world and leave no trace on the record — so its number is
 * permanently zero no matter how much work gets done.
 * ========================================================================= */
export const STAGES = [
  {
    id: 'owned', label: 'Assigned to a rep', system: 'HubSpot',
    what: 'Somebody is accountable. Nothing after this can be chased without it.',
    field: 'sr_assigned_rep', exists: true,
    test: (r) => Boolean(S(r.sr_assigned_rep)),
  },
  {
    id: 'artwork', label: 'Artwork produced', system: 'HubSpot',
    what: 'The minimum standard — every opportunity gets a piece of art.',
    field: 'ready_for_artwork', exists: true,
    /* ⚠️ MEASURABLE BUT NEVER SET — 0 of 5,000. The field exists, so this is a
     * process gap, not a schema gap, and the two need different fixes. */
    test: (r) => Boolean(S(r.ready_for_artwork)),
  },
  {
    id: 'outreach', label: 'Outreach sent', system: 'HubSpot',
    what: 'First contact — the email or sequence enrolment.',
    field: null, exists: false, test: () => false,
  },
  {
    id: 'callScheduled', label: 'Call scheduled', system: 'HubSpot',
    what: 'They booked. The first real signal of interest, and the one most worth measuring separately from a call HELD — the gap between the two is where a pipeline quietly dies.',
    field: null, exists: false, test: () => false,
  },
  {
    id: 'callHeld', label: 'Video call complete', system: 'HubSpot',
    what: 'A real conversation happened.',
    field: null, exists: false, test: () => false,
  },
  {
    id: 'proposal', label: 'Proposal sent', system: 'HubSpot',
    what: 'A priced proposal or PO estimate. Brian: "you have been qualified as a lead, and you have told us you need 37 rings at $265."',
    field: null, exists: false, test: () => false,
  },
  {
    id: 'closed', label: 'Closed won', system: 'HubSpot',
    what: 'The order is placed.',
    field: 'sr_pursuit_status', exists: true,
    test: (r) => /won|crowned/i.test(S(r.sr_pursuit_status)),
  },
  {
    /* ⚠️ FROM HERE THE SPINE LEAVES HUBSPOT, and that is the whole reason this
     * list exists rather than a HubSpot pipeline report.
     *
     * ClickUp is the source of truth for ORDERS; HubSpot is the CRM mirror. A
     * delivery date that lives only in HubSpot would be a copy of a copy, and
     * the flow-of-truth rule says the mirror is never the answer. */
    id: 'delivered', label: 'Delivered', system: 'ClickUp + Shippo',
    what: 'Physically in their hands. Tracked as the In Hands Date off Shippo, against the ClickUp order.',
    field: null, exists: false, test: () => false,
  },
  {
    id: 'paid', label: 'Paid', system: 'QuickBooks',
    what: 'The invoice cleared. Lives in QBO, not the CRM — which is exactly the integration being scoped with Zipcio.',
    field: null, exists: false, test: () => false,
  },
  {
    /* ⚠️ THE TRIGGER IS DELIVERY, NEVER PAYMENT. A customer asked for a review
     * because an invoice cleared is being asked by accounts receivable; asked
     * because the rings arrived, they are being asked at the one moment they
     * are holding the thing they bought. */
    id: 'reviewed', label: 'Reviewed', system: 'Reviews',
    what: 'A review was requested and left. Triggered by physical possession, not by payment.',
    field: null, exists: false, test: () => false,
  },
]

/**
 * Coverage across a set of opportunities.
 *
 * ⚠️ EVERY STAGE REPORTS `measurable` ALONGSIDE ITS COUNT. A zero because
 * nobody has done the work and a zero because there is nowhere to record it
 * look identical on a dashboard and require completely different responses —
 * one is a management problem, the other is a schema problem. Collapsing them
 * is exactly the failure Brian's rule is about.
 */
export function coverageOf(records = []) {
  const total = records.length
  const stages = STAGES.map((st) => {
    const count = st.exists ? records.filter((r) => st.test(r.raw || r)).length : 0
    return {
      id: st.id,
      label: st.label,
      what: st.what,
      field: st.field,
      system: st.system,
      measurable: st.exists,
      count,
      pct: total ? Math.round((count / total) * 1000) / 10 : 0,
      /* The honest reading of the number. */
      reading: !st.exists
        ? `Not measurable — nothing in ${st.system} records this, so the count can never move.`
        : count === 0
          ? 'Measurable, and currently zero — the field exists and nothing has been written to it.'
          : 'Measured.',
    }
  })
  return {
    total,
    stages,
    /* ⚠️ THE HEADLINE IS OWNERSHIP, because it gates everything after it. An
     * unowned opportunity cannot be worked, chased, or reported on. */
    owned: stages.find((s) => s.id === 'owned')?.count ?? 0,
    unowned: total - (stages.find((s) => s.id === 'owned')?.count ?? 0),
    measurableStages: stages.filter((s) => s.measurable).length,
    blindStages: stages.filter((s) => !s.measurable).length,
  }
}

/**
 * Output per rep — "Jonathan has put out 17 proposals this week."
 *
 * ⚠️ RETURNS WHAT IT CAN AND SAYS WHAT IT CANNOT. Proposals and calls have no
 * field, so a per-rep proposal count is not available at any price today. The
 * function reports ownership and artwork per rep and flags the rest as blind
 * rather than returning zeroes that read like poor performance.
 */
export function repOutput(records = []) {
  const byRep = new Map()
  for (const r of records) {
    const raw = r.raw || r
    const rep = S(raw.sr_assigned_rep)
    if (!rep) continue
    if (!byRep.has(rep)) byRep.set(rep, { rep, owned: 0, artwork: 0, won: 0 })
    const row = byRep.get(rep)
    row.owned += 1
    if (S(raw.ready_for_artwork)) row.artwork += 1
    if (/won|crowned/i.test(S(raw.sr_pursuit_status))) row.won += 1
  }
  return {
    reps: [...byRep.values()].sort((a, b) => b.owned - a.owned),
    /* Named so a caller cannot render a proposal column that would always be
     * empty and read as nobody doing any work. */
    blind: STAGES.filter((s) => !s.exists).map((s) => s.label),
  }
}

/* =========================================================================
 * ⚠️ THE SPEC — what has to exist on the championship object before the funnel
 * is measurable. This is the deliverable, not a wish list: each is one HubSpot
 * property, and until they exist the corresponding number is structurally
 * unavailable.
 *
 * Deliberately DATES, not booleans. "Proposal sent" as a checkbox answers
 * whether; a date answers whether AND when AND lets you say "17 this week",
 * which is the actual question a manager asks. A boolean cannot be filtered
 * into a week.
 * ========================================================================= */
export const REQUIRED_PROPERTIES = [
  { name: 'sr_outreach_sent_at', type: 'date', system: 'HubSpot',
    why: 'First contact. Turns "100% outreach" from an aspiration into a number.' },
  { name: 'sr_artwork_sent_at', type: 'date', system: 'HubSpot',
    why: 'The minimum standard. `ready_for_artwork` says it is READY; this says it WENT. Without it there is no way to say how many went out this week.' },
  { name: 'sr_call_scheduled_at', type: 'date', system: 'HubSpot',
    why: 'Booked. Kept separate from held on purpose — the gap between scheduled and held is where a pipeline quietly dies, and one field cannot show a gap.' },
  { name: 'sr_call_held_at', type: 'date', system: 'HubSpot',
    why: 'The conversation happened.' },
  { name: 'sr_proposal_sent_at', type: 'date', system: 'HubSpot',
    why: 'The conversion event a manager runs the week on.' },
  { name: 'sr_proposal_value', type: 'number', system: 'HubSpot',
    why: 'Pipeline dollars. A proposal count with no value cannot be forecast against.' },
  { name: 'sr_delivered_at', type: 'date', system: 'ClickUp → mirror',
    why: 'In-hands date. ⚠️ ClickUp owns the order, so this is MIRRORED into HubSpot for reporting and never authored there — the order system stays the source of truth.' },
  { name: 'sr_paid_at', type: 'date', system: 'QuickBooks → mirror',
    why: 'Invoice cleared. Same rule: QBO owns it, HubSpot mirrors it for the funnel view.' },
  { name: 'sr_review_requested_at', type: 'date', system: 'Reviews',
    why: 'When we asked. Triggered by DELIVERY, never payment — a review asked because an invoice cleared is asked by accounts receivable.' },
  { name: 'sr_review_received_at', type: 'date', system: 'Reviews',
    why: 'Whether they actually left one. Requested and received are different numbers and the ratio between them is the one worth managing.' },
]
